// What Ponder offers: the three puzzle families, their ladders of hand-picked puzzles, the endless daily puzzles,
// the hint ladders and the "big idea" behind each family. Pure data and pure functions, no React.

import { MUSCLE, dailyId, parsePuzzleId, rungId, type Family } from "../../shared/model";
import { makeRng } from "../../shared/rng";
import { CLASSIC_L, PICTURE_FRAME, TWO_HALVES, generateCut, type CutSpec } from "./cut/generate";
import { generateConstellation, latticeSpec } from "./stars/generate";
import type { StarsSpec } from "./stars/geometry";
import { generateCoins, makeSpec, type CoinsSpec } from "./coins/model";
import type { LocalData } from "../app/store";

export const LEVEL_NAMES = ["Warm-up", "Steady", "Stretch", "Deep", "Marathon"] as const;

export interface FamilyMeta {
  family: Family;
  name: string;
  muscle: "See" | "Bend" | "Plan";
  pitch: string;
  skill: string;
  cover: string;
  rungNames: string[];
  maxDifficulty: number;
  /** the idea worth taking away, shown after solving */
  idea: string;
  /** something to chew on afterwards */
  further: string;
}

export const FAMILY_META: Record<Family, FamilyMeta> = {
  cut: {
    family: "cut",
    name: "Look-alikes",
    muscle: MUSCLE.cut,
    pitch: "Cut a shape into pieces that all match.",
    skill: "Seeing structure: how a whole hides its parts.",
    cover: "/art/cover-cut.webp",
    rungNames: ["Two look-alikes", "The four Ls", "The picture frame"],
    maxDifficulty: 5,
    idea:
      "Big shapes often hide small copies of themselves. When a shape must be cut into identical pieces, start where you have the least choice: a corner or a narrow neck can only belong to a few kinds of piece. Guess one piece, test it against the rest, and let each failed guess teach you what the piece cannot be.",
    further: "Can you find a second, different way? Some shapes can be cut in several ways, and finding them all is its own puzzle."
  },
  stars: {
    family: "stars",
    name: "Star Trails",
    muscle: MUSCLE.stars,
    pitch: "Connect every star with as few straight lines as you can.",
    skill: "Bending the rules you assumed: which walls did you draw yourself?",
    cover: "/art/cover-stars.webp",
    rungNames: ["Nine stars", "Sixteen stars", "The hidden path"],
    maxDifficulty: 5,
    idea:
      "Nobody said the trail had to stay among the stars. The stars only say where the trail must pass, not where it may turn. Many stuck moments are like this: the wall you were pushing against is one you drew yourself. When you are stuck, list your assumptions and ask which of them were actually given.",
    further: "What is the fewest lines that could link a 5 by 5 grid of stars? Look at 3 by 3 in 4 lines and 4 by 4 in 6 lines: is there a pattern?"
  },
  coin: {
    family: "coin",
    name: "Odd Coin",
    muscle: MUSCLE.coin,
    pitch: "Find the fake coin on a balance scale in as few weighings as possible.",
    skill: "Planning: choosing steps so that no answer leaves you stuck.",
    cover: "/art/cover-coin.webp",
    rungNames: ["Nine coins", "Twenty-seven coins", "The twelve-coin riddle"],
    maxDifficulty: 4,
    idea:
      "A weighing has three answers, not two, so one weighing can split your suspects into three groups. Nine suspects, two weighings: 9 = 3 x 3. Good planning often means noticing how much one step can tell you, and choosing it so that whichever way it goes, you are still on track.",
    further: "Ten coins and two weighings: why can't it be done, whatever you try? How many coins could four weighings handle?"
  }
};

/** something to chew on after solving; for Look-alikes it depends on how many different answers the puzzle has */
export function furtherFor(id: string): string {
  const s = specFor(id);
  if (s?.family === "cut") {
    return s.spec.ways > 1
      ? `This one can be cut in ${s.spec.ways} really different ways (turned or flipped copies don't count as new). Clear the cuts and see how many you can find.`
      : "This one has exactly one answer (turned or flipped copies don't count as new). Why do you think the corners force it?";
  }
  return FAMILY_META[s?.family ?? "cut"].further;
}

export const dailyDifficultyName = (d: number): string => LEVEL_NAMES[Math.max(1, Math.min(5, d)) - 1];

// ---- ids → puzzles ----

export type Spec =
  | { family: "cut"; spec: CutSpec }
  | { family: "stars"; spec: StarsSpec }
  | { family: "coin"; spec: CoinsSpec };

const cache = new Map<string, Spec>();

export function specFor(id: string): Spec | null {
  const hit = cache.get(id);
  if (hit) return hit;
  const p = parsePuzzleId(id);
  if (!p) return null;
  let out: Spec | null = null;
  if (p.kind === "rung") {
    const i = p.rung;
    if (p.family === "cut") out = i === 1 ? { family: "cut", spec: TWO_HALVES } : i === 2 ? { family: "cut", spec: CLASSIC_L } : i === 3 ? { family: "cut", spec: PICTURE_FRAME } : null;
    if (p.family === "stars") {
      out =
        i === 1 ? { family: "stars", spec: latticeSpec(3) }
        : i === 2 ? { family: "stars", spec: latticeSpec(4) }
        : i === 3 ? { family: "stars", spec: generateConstellation("hidden-path", 2) }
        : null;
    }
    if (p.family === "coin") {
      out =
        i === 1 ? { family: "coin", spec: makeSpec(9, 2, "heavy") }
        : i === 2 ? { family: "coin", spec: makeSpec(27, 3, "heavy") }
        : i === 3 ? { family: "coin", spec: makeSpec(12, 3, "either") }
        : null;
    }
  } else {
    const seed = `${p.date}-${p.difficulty}`;
    if (p.difficulty > FAMILY_META[p.family].maxDifficulty) return null;
    if (p.family === "cut") out = { family: "cut", spec: generateCut(seed, p.difficulty) };
    if (p.family === "stars") out = { family: "stars", spec: generateConstellation(seed, p.difficulty) };
    if (p.family === "coin") out = { family: "coin", spec: generateCoins(makeRng("coin|" + seed), p.difficulty) };
  }
  if (out) cache.set(id, out);
  return out;
}

export interface PuzzleInfo {
  id: string;
  family: Family;
  kind: "rung" | "daily";
  title: string;
  level: string;
  statement: string;
  minutes: number;
}

export function describe(id: string): PuzzleInfo | null {
  const p = parsePuzzleId(id);
  const s = specFor(id);
  if (!p || !s) return null;
  const meta = FAMILY_META[p.family];
  if (p.kind === "rung") {
    return {
      id, family: p.family, kind: "rung", title: meta.rungNames[p.rung - 1], level: `Rung ${p.rung}`,
      statement: s.spec.title, minutes: [3, 5, 7][p.rung - 1] ?? 5
    };
  }
  return {
    id, family: p.family, kind: "daily", title: `${meta.name}: fresh puzzle`, level: dailyDifficultyName(p.difficulty),
    statement: s.spec.title, minutes: [3, 4, 6, 8, 10][p.difficulty - 1] ?? 5
  };
}

export const rungCount = (family: Family): number => FAMILY_META[family].rungNames.length;

// ---- hints: questions, never answers ----

export function hintsFor(id: string): string[] {
  const s = specFor(id);
  if (!s) return [];
  if (s.family === "cut") {
    const { size, pieces } = s.spec;
    return [
      `Every piece will have ${size} squares. Count them up: does the shape split into ${pieces} groups of ${size}? Pieces may be turned or flipped over.`,
      "Look at a corner of the shape. Whichever piece owns that corner has to fit into it. What shapes could it be?",
      "Try guessing one piece, then see whether the rest of the shape can be cut into copies of it. A wrong guess is useful: it rules something out."
    ];
  }
  if (s.family === "stars") {
    if (s.spec.kind === "lattice") {
      return [
        `You may use at most ${s.spec.lines} lines and they must join end to end. How many stars could one well-aimed line reach?`,
        "Must a line stop at a star before it turns? Try a line that keeps going past the last star it needs.",
        "The edge of the stars is not a wall. Put a corner of your trail outside the stars, then come back in."
      ];
    }
    return [
      "Some stars line up in threes. Can you spot a group that lies on one straight line?",
      "Pebble has marked a group that lines up. Extend that line beyond its stars and see where another group's line crosses it.",
      "The corners of the hidden trail are where two lines cross. Those crossings are usually not on a star, so aim at empty sky."
    ];
  }
  const either = s.spec.mode !== "heavy";
  return [
    "A weighing has three possible answers, not two: left side down, right side down, or level. How could you use all three?",
    "Try weighing groups of coins, not single coins. Whatever the scale says, you want to be left with fewer suspects.",
    either
      ? "Coins that were on the scale and turned out fine are now known to be genuine. They make handy fillers to even up the pans."
      : "Split the suspects into three groups of about the same size and weigh two of them. If the pans balance, which group hides the fake?"
  ];
}

// ---- today's plan ----

export function difficultyFor(family: Family, data: LocalData): number {
  const solvedDaily = Object.values(data.progress).filter((p) => p.family === family && p.status === "solved" && parsePuzzleId(p.id)?.kind === "daily").length;
  return Math.min(FAMILY_META[family].maxDifficulty, 2 + Math.floor(solvedDaily / 2));
}

/** the first ladder rung not yet attempted, otherwise today's fresh puzzle at the person's own level */
export function proposeFor(family: Family, data: LocalData, day: string): string {
  for (let r = 1; r <= rungCount(family); r++) {
    const rec = data.progress[rungId(family, r)];
    if (!rec || rec.status === "open") return rungId(family, r);
  }
  return dailyId(family, day, difficultyFor(family, data));
}
